/**
 * Fills the Jessie regions of a filament product page.
 *
 * Liquid cannot read window.MCM_COLOURS, so every WP2 block and section renders
 * an empty, labelled shell carrying three attributes — the product handle, the
 * detected line and the colour name parsed out of the title — and this script
 * resolves them through JessieColour.resolve() and fills what it finds. It
 * never invents a value: a row it cannot resolve leaves its shell hidden, and a
 * field with no data has its row removed rather than printing "undefined".
 *
 * Regions, by their data-jessie-pdp value:
 *   colour-id       the buy box card: tile, name, HEX / PMS / FHI / RAL
 *   also-available  the twin chips across the other Jessie lines
 *   references      the full colour references card
 *   print-settings  the PrusaSlicer table, from the profiles asset (no colour)
 *   colour-match    the Color Match callout: ranks in place through JessieMatch
 *
 * Load order (all deferred, in this order): material-colours.js,
 * jessie-colour-extras.js, jessie-print-profiles.js, jessie-colour.js, this.
 *
 * Idempotent by design. A block may be on the page twice, so this src may be
 * emitted twice and a repeated src re-executes; and _filament-color-switcher
 * swaps the whole product section over AJAX and dispatches
 * `product:section-swapped`, after which the shells are new and unfilled.
 * Every region is marked `data-jessie-filled` once handled and skipped after.
 * The swap only re-renders the buy box, so the regions outside it are re-keyed
 * to the new colour before that pass — see resync() — or hidden if the new
 * colour resolves to nothing.
 */
if (window.__jessie_product) {
  /* Already loaded. A later block on the page emits this src again, and by then
     an asset that had not loaded on the first pass may be there — the print
     profiles, say, whose <script> sits further down the document than the buy
     box's. So the repeat pass re-runs the filler rather than doing nothing;
     init() skips every region it has already filled. */
  if (window.JessieProduct) window.JessieProduct.init();
} else {
  window.__jessie_product = true;

  (function () {
    'use strict';

    var FILLED = 'data-jessie-filled';

    function api() {
      return window.JessieColour || null;
    }

    /** The row for one shell, from its three join attributes (plan §3.4). */
    function rowFor(el) {
      var J = api();
      if (!J) return null;
      return J.resolve({
        handle: el.getAttribute('data-jessie-handle'),
        line: el.getAttribute('data-jessie-line'),
        name: el.getAttribute('data-jessie-colour'),
      });
    }

    function text(el, value) {
      if (el) el.textContent = value;
    }

    /**
     * A row element either shows a value, says there is no close match, or
     * disappears. Which of the three is the caller's decision, because "no
     * close match" is only true of RAL and Pantone FHI.
     */
    function setRow(scope, key, value, fallback) {
      var row = scope.querySelector('[data-jessie-row="' + key + '"]');
      var field = scope.querySelector('[data-jessie-field="' + key + '"]');
      var shown = value;
      if (!shown && fallback) shown = fallback;
      if (!shown) {
        if (row) row.hidden = true;
        return false;
      }
      if (field) field.textContent = shown;
      if (row) row.hidden = false;
      return true;
    }

    /** Elements whose copy carries {n} / {line} / {line_label} tokens. */
    function applyFills(scope, line) {
      var J = api();
      if (!J) return;
      var vars = {
        line: line || '',
        line_label: line ? J.lineLabel(line) : '',
        n: line ? J.line(line).length : '',
      };
      if (!vars.n) vars.n = '';
      var nodes = scope.querySelectorAll('[data-jessie-fill]');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].textContent = J.fill(nodes[i].getAttribute('data-jessie-fill'), vars);
      }
    }

    /** The tile shell inside a region, painted with the row we resolved. */
    function paintTile(scope, row) {
      var J = api();
      var tile = scope.querySelector('[data-jessie-tile]');
      if (!J || !tile || !row) return;
      /* The shell was rendered with the product handle, because Liquid cannot
         know the colour table's id. Now that we do, say so in the DOM. */
      tile.setAttribute('data-jessie-tile', row.id);
      J.paintTile(tile, row);
    }

    function noMatchLabel(el) {
      return el.getAttribute('data-jessie-no-match-label') || '';
    }

    /* ------------------------------------------------------------ colour ID */

    function fillColourId(el) {
      var J = api();
      var row = rowFor(el);
      if (!J || !row) return false;
      var full = J.withExtras(row);
      var none = noMatchLabel(el);

      text(el.querySelector('[data-jessie-field="name"]'), full.name);
      setRow(el, 'hex', full.hex);
      setRow(el, 'pms', full.pantone, none);
      setRow(el, 'fhi', full.fhi, none);
      setRow(el, 'ral', full.ral, none);

      var badge = el.querySelector('[data-jessie-row="outside"]');
      if (badge) badge.hidden = full.outside_srgb !== true;

      paintTile(el, full);
      applyFills(el, full.line);
      el.hidden = false;
      return true;
    }

    /* ------------------------------------------------------- also available */

    function fillAlsoAvailable(el) {
      var J = api();
      var row = rowFor(el);
      if (!J || !row) return false;
      var twins = J.twins(row);
      if (!twins.length) return false;

      var host = el.querySelector('[data-jessie-chips]');
      if (!host) return false;
      var max = parseInt(el.getAttribute('data-jessie-max'), 10);
      if (!(max > 0)) max = 3;

      host.textContent = '';
      var shown = 0;
      for (var i = 0; i < twins.length && shown < max; i++) {
        var twin = twins[i];
        if (!twin.url) continue;
        var chip = document.createElement('a');
        chip.className = 'jessie-also__chip';
        chip.href = twin.url;

        var dot = document.createElement('span');
        dot.className = 'swatch';
        if (twin.hex) dot.style.setProperty('--swatch-background', twin.hex);
        chip.appendChild(dot);

        var label = document.createElement('span');
        label.textContent = J.lineLabel(twin.line) || twin.line;
        chip.appendChild(label);

        host.appendChild(chip);
        shown += 1;
      }
      if (!shown) return false;
      el.hidden = false;
      return true;
    }

    /* ----------------------------------------------------------- references */

    function fillReferences(el) {
      var J = api();
      var row = rowFor(el);
      if (!J || !row) return false;
      var full = J.withExtras(row);
      var none = noMatchLabel(el);

      var names = el.querySelectorAll('[data-jessie-field="name"]');
      for (var i = 0; i < names.length; i++) names[i].textContent = full.name;
      var hexes = el.querySelectorAll('[data-jessie-field="hex"]');
      for (var h = 0; h < hexes.length; h++) hexes[h].textContent = full.hex || '';

      setRow(el, 'hex', full.hex);
      setRow(el, 'rgb', rgbText(J, full.hex));
      setRow(el, 'cmyk', cmykText(full.cmyk));
      setRow(el, 'pms', full.pantone, none);
      setRow(el, 'fhi', full.fhi, none);
      setRow(el, 'ral', full.ral, none);
      setRow(el, 'lab', labText(full.lab_d50));

      var badge = el.querySelector('[data-jessie-row="outside"]');
      if (badge) badge.hidden = full.outside_srgb !== true;

      var panel = el.querySelector('[data-jessie-panel]');
      if (panel && full.hex) panel.classList.toggle('is-light', J.isLight(full.hex));

      paintTile(el, full);
      applyFills(el, full.line);
      el.hidden = false;
      return true;
    }

    function rgbText(J, hex) {
      var rgb = J.hexToRgb(hex);
      if (!rgb) return '';
      return rgb[0] + ' · ' + rgb[1] + ' · ' + rgb[2];
    }

    function cmykText(cmyk) {
      if (!cmyk || cmyk.length !== 4) return '';
      var out = [];
      for (var i = 0; i < 4; i++) {
        var n = Number(cmyk[i]);
        if (isNaN(n)) return '';
        out.push(String(Math.round(n)));
      }
      return out.join(' / ');
    }

    /**
     * The MEASURED D50/2° reading, which is why this only ever renders
     * `lab_d50` from the extras asset. MCM's own `lab` is computed from the hex
     * for the Color Match tool's ΔE maths and is a different number.
     */
    function labText(lab) {
      if (!lab || lab.length !== 3) return '';
      var L = Number(lab[0]);
      var a = Number(lab[1]);
      var b = Number(lab[2]);
      if (isNaN(L) || isNaN(a) || isNaN(b)) return '';
      /* Middots, like the RGB row: one visible separator, no invisible
         characters in the source and nothing that collapses in HTML. */
      return 'L* ' + L.toFixed(1) + ' · a* ' + signed(a) + ' · b* ' + signed(b);
    }

    /**
     * A CIELAB coordinate with its sign. The negative sign is a true minus
     * (U+2212), not a hyphen: this is a number beside "a*" and "b*", and a
     * hyphen renders short, sits at the wrong height and can be read as a
     * word-break by a screen reader.
     */
    function signed(n) {
      var v = Math.abs(n).toFixed(1);
      if (Number(v) === 0) return v; // a rounded zero has no sign either way
      return (n < 0 ? '−' : '+') + v;
    }

    /* ------------------------------------------------------- print settings */

    function fillPrintSettings(el) {
      var J = api();
      if (!J || !J.profiles) return false;
      var line = el.getAttribute('data-jessie-line');
      var profile = J.profiles.forLine(line);
      if (!profile || !profile.rows || !profile.rows.length) return false;

      var body = el.querySelector('[data-jessie-rows]');
      if (!body) return false;
      body.textContent = '';
      for (var i = 0; i < profile.rows.length; i++) {
        var row = profile.rows[i];
        var tr = document.createElement('tr');
        appendCell(tr, row.label);
        appendCell(tr, row.nozzle);
        appendCell(tr, row.bed);
        appendCell(tr, row.fan);
        body.appendChild(tr);
      }

      var note = el.querySelector('[data-jessie-note]');
      if (note && profile.note) {
        note.textContent = profile.note;
        note.hidden = false;
      }

      var source = el.querySelector('[data-jessie-source]');
      if (source && profile.source) {
        var prefix = source.getAttribute('data-jessie-source-prefix') || '';
        source.textContent = (prefix ? prefix + ' ' : '') + profile.source + '.';
        source.hidden = false;
      }

      applyFills(el, line);
      el.hidden = false;
      return true;
    }

    function appendCell(tr, value) {
      var td = document.createElement('td');
      td.textContent = value == null ? '' : value;
      tr.appendChild(td);
    }

    /* --------------------------------------------------------- colour match */

    /**
     * The callout matches in place. Parsing, ΔE2000 ranking and the gated
     * rendering are assets/jessie-match.js, shared with the featured pages'
     * Color Match band, so the two surfaces cannot drift; this only mounts it
     * and lets the region be marked filled.
     *
     * The small "Open Color Match" link still hands off to the full tool, and
     * jessie-match.js writes the typed colour to sessionStorage before it goes.
     */
    function wireColourMatch(el) {
      if (!window.JessieMatch) return false;
      /* mount() returns null for a region the matcher's own safety-net init()
         already wired; repaint it, since that pass can precede the colour
         table's arrival. */
      if (!window.JessieMatch.mount(el)) window.JessieMatch.render(el);
      return true;
    }

    /* ------------------------------------------------------------------ run */

    var HANDLERS = {
      'colour-id': fillColourId,
      'also-available': fillAlsoAvailable,
      references: fillReferences,
      'print-settings': fillPrintSettings,
      'colour-match': wireColourMatch,
    };

    function init() {
      var regions = document.querySelectorAll('[data-jessie-pdp]');
      for (var i = 0; i < regions.length; i++) {
        var el = regions[i];
        if (el.hasAttribute(FILLED)) continue;
        var handler = HANDLERS[el.getAttribute('data-jessie-pdp')];
        if (!handler) continue;
        var done = false;
        try {
          done = handler(el);
        } catch (error) {
          /* A broken row must not take the rest of the page with it. */
          if (window.console && console.warn) console.warn('[jessie-product]', error);
          done = false;
        }
        /* Only a region that actually filled is marked done: a region left
           unfilled because the colour table has not loaded yet gets another
           chance on the next run. */
        if (done) el.setAttribute(FILLED, '');
      }
    }

    /* ---------------------------------------------------------- re-keying */

    /**
     * The colour switcher fetches only the product-information section, so the
     * buy box comes back as new, unfilled shells carrying the new colour —
     * while every Jessie region *outside* that section still carries the old
     * colour's join attributes and its data-jessie-filled marker. Left alone,
     * the page contradicts itself: the full-width references card keeps
     * shouting "Pure Cyan · #0081AF" under a buy box that now says White.
     *
     * So after a swap the new colour is read off the freshly rendered Color ID
     * shell (it is inside the swapped section, so it is the authority; the URL
     * the switcher pushed is the fallback when that block is not on the page),
     * every stale region is re-keyed and filled again, and a region whose new
     * colour cannot be resolved is hidden rather than left showing the last one.
     */
    function currentKeys() {
      var source = document.querySelector('[data-jessie-pdp="colour-id"][data-jessie-handle]');
      if (source) {
        var handle = source.getAttribute('data-jessie-handle');
        if (handle) {
          return {
            handle: handle,
            line: source.getAttribute('data-jessie-line') || '',
            colour: source.getAttribute('data-jessie-colour') || '',
          };
        }
      }
      var J = api();
      if (!J) return null;
      var path = String(window.location.pathname || '');
      var match = path.match(/\/products\/([^/?#]+)/);
      if (!match) return null;
      var row = J.resolve({ handle: match[1] });
      if (!row) return null;
      return { handle: match[1], line: row.line || '', colour: row.name || '' };
    }

    /** Put one region back in the state its Liquid shipped, on a new colour. */
    function rekey(el, keys) {
      el.setAttribute('data-jessie-handle', keys.handle);
      el.setAttribute('data-jessie-line', keys.line);
      el.setAttribute('data-jessie-colour', keys.colour);
      el.removeAttribute(FILLED);

      var tiles = el.querySelectorAll('[data-jessie-tile]');
      for (var i = 0; i < tiles.length; i++) {
        var tile = tiles[i];
        tile.setAttribute('data-jessie-tile', keys.handle);
        /* The finish overlay is a class paintTile added for the *old* colour;
           a silk spool followed by a matte one would keep the sheen. */
        var classes = String(tile.className || '').split(/\s+/);
        for (var c = 0; c < classes.length; c++) {
          if (classes[c].indexOf('jessie-tile--') === 0) tile.classList.remove(classes[c]);
        }
        tile.style.removeProperty('--jessie-tile-color');
        tile.hidden = true;
      }
    }

    function resync() {
      var keys = currentKeys();
      if (!keys) return;
      var regions = document.querySelectorAll('[data-jessie-pdp]');
      var touched = [];
      for (var i = 0; i < regions.length; i++) {
        var el = regions[i];
        /* The Color Match callout carries no colour at all, and a region
           already on the new handle is either fresh markup or up to date. */
        if (!el.hasAttribute('data-jessie-handle')) continue;
        if (el.getAttribute('data-jessie-handle') === keys.handle) continue;
        rekey(el, keys);
        touched.push(el);
      }
      if (!touched.length) return;
      init();
      for (var t = 0; t < touched.length; t++) {
        /* Nothing resolved for the new colour: better an absent card than one
           that still describes the colour the shopper just navigated away from. */
        if (!touched[t].hasAttribute(FILLED)) touched[t].hidden = true;
      }
    }

    /* The colour switcher replaces the whole product section over AJAX, so the
       shells inside it are new markup with no data-jessie-filled on them, and
       the ones outside it are stale. */
    function onSwapped() {
      init();
      try {
        resync();
      } catch (error) {
        if (window.console && console.warn) console.warn('[jessie-product]', error);
      }
    }

    window.addEventListener('product:section-swapped', onSwapped);

    window.JessieProduct = { init: init, resync: resync };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
    /* Last pass, once every deferred asset on the page has run. */
    window.addEventListener('load', init);
  })();
}
